import classNames from 'classnames';
import isNil from 'lodash-es/isNil';
import { getHours, getMinutes, set, format, isMatch } from 'date-fns';
import React, { useMemo, useRef, useState, useEffect } from 'react';
import { Input } from '../input';
import { CSSValueWithLength, getZIndex } from '../../styles';
import { useProviderConfig } from '../provider';
import { TimePickerIcon } from './TimePickerIcon';
import { TimePickerOptions } from './TimePickerOptions';
import { TimeChipInput } from './TimeChipInput';
import { Stack } from '../stack';
import { TimePickerOverlay } from './TimePickerOverlay';
import { BottomSheet } from '../bottom-sheet';
import { Divider } from '../divider';
import { Button } from '../button';
import { getTimePickerInputStyleBySize, time_picker_group_css } from './styles';
import { InputSize, InputStatus } from '../input/types';
import styled from '@emotion/styled';
import { semantic_colors, semantic_spacing, shapes } from '../../foundation';
import { FormHelperText } from '../form';
import { getAvailableSelectedTime, getDisabledTime, getDisabledTimes, getTimePickerOptions } from './utils';

export interface DisabledTimes {
  disabledHours?: () => number[];
  disabledMinutes?: (hour: number) => number[];
}

export type TimePickerDisplayMode = 'popover' | 'bottom-sheet';

export interface TimePickerProps {
  className?: string;
  /**
   * Input 너비를 지정할 수 있습니다.
   *
   * @default '100%'
   */
  width?: CSSValueWithLength;
  flex?: React.CSSProperties['flex'];
  /** @default getZIndex('picker') */
  zIndex?: number;
  status?: InputStatus;
  disabled?: boolean;
  /** @default false */
  readOnly?: boolean;
  disabledTime?: (value: Date | null) => DisabledTimes;
  /**
   * TimePicker 팝오버 너비 100% 사용여부
   *  - Input 요소 만큼의 100% 너비를 가집니다.
   *  - true 일 경우 footer(확인/취소) 버튼 속성은 자동 fill 처리됩니다.
   *
   * @default false
   */
  fill?: boolean;
  /**
   * TimePicker가 노출되는 모드입니다.
   *  - 'bottom-sheet'일 경우 모바일 BottomSheet 안에서 시/분 SelectChip 행으로 선택합니다.
   * @default 'popover'
   */
  displayMode?: TimePickerDisplayMode;
  /** @default 'medium' */
  size?: InputSize;
  value?: Date;
  onClose?: () => void;
  onOpen?: () => void;
  onChange?: (value: Date | null) => void;
  /**
   * 지정된 배수 단위로 분 설정이 가능합니다.
   *
   * @default 1
   */
  incrementMinute?: number;
  /**
   * 비활성화된 분 항목을 목록에서 숨길지 여부
   *
   * true: disabledMinutes로 비활성화된 분 항목이 목록에서 완전히 제거됩니다.
   * false: 비활성화된 분 항목이 목록에 표시되지만 선택 불가능합니다.
   *
   * @default false
   */
  hideDisabledMinutes?: boolean;
  /**
   * Input 우측에 값 지우기 버튼 표시 여부
   *
   * true: 값이 있을 때 마우스 호버 시(모바일에서는 호버 없이) 지우기(X) 버튼 표시
   * false: 항상 시계 아이콘만 표시
   * 값이 없을 때는 설정과 관계없이 시계 아이콘이 표시됩니다.
   *
   * @default false
   */
  hasRemoveButton?: boolean;
  /** @default true */
  usePortal?: boolean;
}

const DATE_FORMAT = 'HH:mm';

export const TimePicker = ({
  disabled,
  readOnly = false,
  disabledTime,
  size = 'medium',
  value,
  status,
  width = '100%',
  flex,
  fill = false,
  displayMode = 'popover',
  zIndex = getZIndex('picker'),
  className,
  onOpen,
  onClose,
  onChange,
  incrementMinute,
  hideDisabledMinutes,
  hasRemoveButton = false,
  usePortal = true,
}: TimePickerProps) => {
  const { locale } = useProviderConfig();
  const error = status === 'error';
  const isBottomSheet = displayMode === 'bottom-sheet';
  const [open, setOpen] = useState(false);
  const [showRemoveIcon, setShowRemoveIcon] = useState(false);
  const [selectedTimeHour, setSelectedTimeHour] = useState<number>();
  const [selectedTimeMinute, setSelectedTimeMinute] = useState<number>();
  const [inputValue, setInputValue] = useState<string>('');

  const closingByConfirmRef = useRef(false);

  const dateTime = useMemo(() => {
    if (isNil(selectedTimeHour) || isNil(selectedTimeMinute)) return null;

    const baseDate = value ?? new Date();
    return set(baseDate, { hours: selectedTimeHour, minutes: selectedTimeMinute, seconds: 0 });
  }, [selectedTimeHour, selectedTimeMinute]);

  const { timeHourPickerOptions, timeMinutePickerOptions } = useMemo(() => {
    const currentTime = set(new Date(), {
      hours: selectedTimeHour ?? undefined,
      minutes: selectedTimeMinute ?? undefined,
      seconds: 0,
    });

    const disabledTimes = getDisabledTimes(disabledTime, currentTime);

    return {
      timeHourPickerOptions: getTimePickerOptions(24, 1, disabledTimes.disabledHours),
      timeMinutePickerOptions: getTimePickerOptions(
        60,
        incrementMinute,
        disabledTimes.disabledMinutes,
        hideDisabledMinutes,
      ),
    };
  }, [disabledTime, selectedTimeHour, selectedTimeMinute, incrementMinute, hideDisabledMinutes]);

  const disabledSelectedTime = useMemo(() => {
    if (isNil(selectedTimeHour) || isNil(selectedTimeMinute)) {
      return false;
    }

    return Boolean(
      getDisabledTime(timeHourPickerOptions, Number(selectedTimeHour)) ||
        getDisabledTime(timeMinutePickerOptions, Number(selectedTimeMinute)) ||
        !timeMinutePickerOptions.find((option) => option.value === Number(selectedTimeMinute)),
    );
  }, [timeHourPickerOptions, timeMinutePickerOptions, selectedTimeHour, selectedTimeMinute]);

  const mobileConfirmDisabled = isNil(selectedTimeHour) || isNil(selectedTimeMinute) || disabledSelectedTime;

  const selectedLabel = useMemo(() => {
    if (inputValue) {
      return inputValue;
    }

    if (!dateTime) {
      return null;
    }

    return format(dateTime, DATE_FORMAT);
  }, [dateTime, inputValue]);

  const handleInitial = () => {
    setSelectedTimeHour(undefined);
    setSelectedTimeMinute(undefined);
    setInputValue('');
  };

  const handleChangeHour = (value: number) => {
    setSelectedTimeHour(value);
    !selectedTimeMinute && setSelectedTimeMinute(getAvailableSelectedTime(timeMinutePickerOptions));
    setInputValue('');
  };

  const handleChangeMinute = (value: number) => {
    setSelectedTimeMinute(value);
    !selectedTimeHour && setSelectedTimeHour(getAvailableSelectedTime(timeHourPickerOptions));
    setInputValue('');
  };

  const updateSelectedTime = () => {
    if (value) {
      const dateHour = getHours(value);
      const dateMinute = getMinutes(value);
      if (selectedTimeHour === dateHour && selectedTimeMinute === dateMinute) return;

      setSelectedTimeHour(dateHour);
      setSelectedTimeMinute(dateMinute);
      setInputValue(format(value, DATE_FORMAT));
    } else {
      handleInitial();
    }
  };

  const checkInputValidation = (value: string, dateFormat: string) => {
    if (!value) {
      return false;
    }

    if (!/^([01][0-9]|2[0-3]):[0-5][0-9]$/.test(value)) {
      return false;
    }

    if (value.length < dateFormat.length) {
      return true;
    }

    if (!isMatch(value, dateFormat)) {
      return false;
    }

    return value.length === dateFormat.length;
  };

  const checkValidation = (value: string) => {
    if (!checkInputValidation(value, DATE_FORMAT)) {
      return false;
    }

    return true;
  };

  useEffect(() => {
    updateSelectedTime();
  }, [value]);

  useEffect(() => {
    if (inputValue) {
      const [hour, minute] = inputValue.trim().split(':');

      if (checkValidation(inputValue) && minute?.length === 2) {
        setSelectedTimeHour(Number(hour));
        setSelectedTimeMinute(Number(minute));
      } else {
        setSelectedTimeHour(undefined);
        setSelectedTimeMinute(undefined);
      }
    }
  }, [inputValue]);

  const handleOpenSheet = () => {
    if (disabled) return;
    closingByConfirmRef.current = false;
    onOpen?.();
    setOpen(true);
  };

  const handleSheetClose = () => {
    if (!closingByConfirmRef.current) {
      updateSelectedTime();
    }
    setOpen(false);
    onClose?.();
  };

  const handleConfirm = () => {
    if (mobileConfirmDisabled) return;
    closingByConfirmRef.current = true;
    onChange?.(dateTime);
    setOpen(false);
  };

  const handleCancel = () => {
    setOpen(false);
  };

  const removeIconVisible = !disabled && Boolean(value) && (isBottomSheet ? hasRemoveButton : Boolean(showRemoveIcon));

  const renderInputArea = (onActivate: () => void, inputReadOnly: boolean) => (
    <Stack direction='column' flex={flex} gap={semantic_spacing.helper_text.gap.desktop}>
      <Container
        direction='row'
        tabIndex={0}
        onMouseEnter={() => hasRemoveButton && setShowRemoveIcon(true)}
        onMouseLeave={() => hasRemoveButton && setShowRemoveIcon(false)}
        onClick={onActivate}
        onKeyDown={(e) => e.key === 'Enter' && !open && onActivate()}
        width={width}
      >
        <Input
          tabIndex={-1}
          flex={1}
          autoComplete='off'
          disabled={disabled}
          value={selectedLabel ?? ''}
          status={error || disabledSelectedTime ? 'error' : 'info'}
          placeholder={locale?.TimePicker.placeholder}
          size={size}
          style={getTimePickerInputStyleBySize(size)}
          htmlSize={DATE_FORMAT.length}
          rightIcon={
            <TimePickerIcon
              showRemoveIcon={removeIconVisible}
              onClick={() => {
                handleInitial();
                onChange?.(null);
              }}
            />
          }
          readOnly={inputReadOnly}
          onChange={(e) => {
            const value = e.target.value;
            setInputValue(value);

            if (value === '') {
              setSelectedTimeHour(undefined);
              setSelectedTimeMinute(undefined);
            }
          }}
          onKeyDown={(e) => {
            const value = e.currentTarget.value;
            if (e.key === 'Enter' && checkValidation(value)) {
              setOpen(false);
              onChange?.(dateTime);
            }
          }}
          className={classNames('pds-time-picker-input', className)}
        />
      </Container>
      {disabledSelectedTime && <FormHelperText status='error'>다른 시간을 선택해주세요.</FormHelperText>}
    </Stack>
  );

  if (isBottomSheet) {
    return (
      <>
        {renderInputArea(handleOpenSheet, true)}
        <BottomSheet
          open={open}
          onClose={handleSheetClose}
          shouldCloseOnDrag={false}
          zIndex={zIndex}
          contentProps={{ p: '0' }}
        >
          {() => (
            <Stack direction='column' align='stretch'>
              <Stack direction='column' p={16} align='stretch'>
                <TimeChipInput
                  value={dateTime}
                  onChange={(next) => {
                    setSelectedTimeHour(getHours(next));
                    setSelectedTimeMinute(getMinutes(next));
                    setInputValue('');
                  }}
                  disabledTime={disabledTime}
                  incrementMinute={incrementMinute}
                  hideDisabledMinutes={hideDisabledMinutes}
                />
              </Stack>
              <Divider />
              <Stack direction='row' gap={8} p={16}>
                <Button size='xlarge' fill onClick={handleCancel}>
                  {locale?.TimePicker.footer.clear}
                </Button>
                <Button
                  variant='filled'
                  color='primary'
                  size='xlarge'
                  fill
                  disabled={mobileConfirmDisabled}
                  onClick={handleConfirm}
                >
                  {locale?.TimePicker.footer.ok}
                </Button>
              </Stack>
            </Stack>
          )}
        </BottomSheet>
      </>
    );
  }

  return (
    <TimePickerOverlay
      opened={open}
      setOpened={setOpen}
      onOpen={onOpen}
      onClose={onClose}
      onCloseOutsidePress={updateSelectedTime}
      disabled={disabled}
      fill={fill}
      zIndex={zIndex}
      usePortal={usePortal}
      content={({ close, availableHeight }) => (
        <Stack direction='column' gap={8} css={time_picker_group_css} align={fill ? 'center' : 'flex-end'}>
          <TimePickerOptions
            availableHeight={availableHeight}
            timeHourPickerOptions={timeHourPickerOptions}
            timeMinutePickerOptions={timeMinutePickerOptions}
            fill={fill}
            selectedTimeHour={selectedTimeHour}
            selectedTimeMinute={selectedTimeMinute}
            onChangeHour={handleChangeHour}
            onChangeMinute={handleChangeMinute}
          />

          <Stack direction='row' gap={8} p={8} width={fill ? '100%' : 'auto'}>
            <Button
              fill={fill}
              onClick={() => {
                updateSelectedTime();
                close();
              }}
            >
              {locale?.TimePicker.footer.clear}
            </Button>
            <Button
              variant='filled'
              color='primary'
              fill={fill}
              disabled={!dateTime || disabledSelectedTime}
              onClick={() => {
                onChange?.(dateTime);
                close();
              }}
            >
              {locale?.TimePicker.footer.ok}
            </Button>
          </Stack>
        </Stack>
      )}
    >
      {renderInputArea(() => !disabled && setOpen(true), readOnly || !open)}
    </TimePickerOverlay>
  );
};

const Container = styled(Stack)`
  &:focus {
    outline: none;
  }
  &:focus-visible {
    ${shapes.border_radius.small};
    outline: 3px solid ${semantic_colors.tab_focus};
  }
`;
